export const PLAYBACK_API = "/__playback_proxy__";

export const ZOOM_PRESETS = [
  { key: "day", label: "24시간", seconds: 24 * 60 * 60 },
  { key: "hour", label: "1시간", seconds: 60 * 60 },
  { key: "10min", label: "10분", seconds: 10 * 60 },
  { key: "1min", label: "1분", seconds: 60 },
];

export const PLAYBACK_SPEEDS = [0.5, 1, 1.5, 2];

export const WEEK_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

export function formatDateDisplay(value) {
  if (!value) return "-";

  const [y, m, d] = String(value).split("-");
  if (!y || !m || !d) return value;

  return `${y}. ${Number(m)}. ${Number(d)}.`;
}

export function formatMonthTitle(value) {
  if (!value) return "-";

  const [y, m] = String(value).split("-");
  if (!y || !m) return value;

  return `${y}. ${Number(m)}.`;
}

export function formatTimeDisplay(value) {
  if (!value) return "-";

  const [hh = "00", mm = "00"] = String(value).split(":");
  const hour = Number(hh);
  const minute = String(mm).padStart(2, "0");
  const isAM = hour < 12;
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;

  return `${isAM ? "오전" : "오후"} ${displayHour}:${minute}`;
}

export function toYearMonth(dateStr) {
  return String(dateStr || "").slice(0, 7);
}

export function shiftYearMonth(yearMonth, delta) {
  const [year, month] = String(yearMonth || "").split("-").map(Number);

  if (!year || !month) return yearMonth;

  const d = new Date(year, month - 1 + delta, 1);

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function buildCalendarCells(yearMonth) {
  const [year, month] = String(yearMonth || "").split("-").map(Number);

  if (!year || !month) return [];

  const firstDay = new Date(year, month - 1, 1);
  const startWeekday = firstDay.getDay();
  const lastDate = new Date(year, month, 0).getDate();

  const cells = [];

  for (let i = 0; i < startWeekday; i += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= lastDate; day += 1) {
    cells.push(
      `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
        2,
        "0"
      )}`
    );
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return cells;
}

export function getZoomSeconds(zoomKey) {
  return ZOOM_PRESETS.find((z) => z.key === zoomKey)?.seconds ?? 3600;
}

export function parseTimeToSec(timeStr) {
  const [h = 0, m = 0, s = 0] = String(timeStr || "00:00:00")
    .split(":")
    .map(Number);

  return h * 3600 + m * 60 + s;
}

export function secToTimeString(sec, withSeconds = true) {
  const safe = Math.max(0, Math.min(86399, Math.floor(sec)));
  const h = String(Math.floor(safe / 3600)).padStart(2, "0");
  const m = String(Math.floor((safe % 3600) / 60)).padStart(2, "0");
  const s = String(safe % 60).padStart(2, "0");

  return withSeconds ? `${h}:${m}:${s}` : `${h}:${m}`;
}

export function secToDateTimeString(selectedDate, sec) {
  return `${selectedDate} ${secToTimeString(sec)}`;
}

export function formatDateTime(value, fallback = "-") {
  if (!value) return fallback;

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;

  return d.toLocaleString("ko-KR", { hour12: false });
}

export function getSegStartSec(seg) {
  const d = new Date(seg.startTime);

  return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
}

export function getSegEndSec(seg) {
  const d = new Date(seg.endTime);

  return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
}

export function getTodayDateString() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
}

export function normalizePlaybackCamera(cam) {
  return {
    id: String(cam.id ?? ""),
    camIdx: Number(cam.camIdx ?? cam.id ?? 0),
    userIdx: Number(cam.userIdx ?? 0),
    camNo: Number(cam.camNo ?? 0),
    camName: cam.camName || "",
    streamKey: String(cam.streamKey ?? "").trim(),
    ownerLoginId: String(cam.ownerLoginId ?? "").trim(),
    ownerUserName: String(cam.ownerUserName ?? "").trim(),
    ownerRoleCode: String(cam.ownerRoleCode ?? "").trim(),

    // "지점 / 팀 / 이름 / 차량번호" 표시용.
    // 여기서 빠뜨리면 백엔드가 내려줘도 화면까지 도달하지 못한다.
    ownerBranchName: String(cam.ownerBranchName ?? "").trim(),
    ownerTeamName: String(cam.ownerTeamName ?? "").trim(),
    ownerVehicleNo: String(cam.ownerVehicleNo ?? "").trim(),
  };
}

export function normalizePlaybackSegment(seg, idx) {
  return {
    id: String(idx + 1),
    fileName: seg.fileName,
    startTime: seg.startTime,
    endTime: seg.endTime,
    playbackUrl: seg.playbackUrl,
    downloadUrl: seg.downloadUrl,
  };
}

export function buildAxisLabels({
  isMobile,
  windowStartSec,
  visibleWindowSeconds,
}) {
  const count = isMobile ? 3 : 6;
  const list = [];

  for (let i = 0; i <= count; i += 1) {
    const sec = windowStartSec + (visibleWindowSeconds / count) * i;
    list.push(secToTimeString(sec, false));
  }

  return list;
}

export function buildTimelineBars({
  segments,
  windowStartSec,
  windowEndSec,
  visibleWindowSeconds,
}) {
  return segments
    .map((seg) => {
      const startSec = getSegStartSec(seg);
      const endSec = getSegEndSec(seg);

      if (endSec < windowStartSec || startSec > windowEndSec) return null;

      const visibleStart = Math.max(startSec, windowStartSec);
      const visibleEnd = Math.min(endSec, windowEndSec);

      const leftPercent =
        ((visibleStart - windowStartSec) / visibleWindowSeconds) * 100;

      const widthPercent = Math.max(
        ((visibleEnd - visibleStart) / visibleWindowSeconds) * 100,
        0.3
      );

      return {
        ...seg,
        leftPercent,
        widthPercent,
        startSec,
        endSec,
      };
    })
    .filter(Boolean);
}

// CCTV/NVR 방식: 인접(간격 <= gapTolSec) 조각들을 하나의 "연속 녹화 구간"으로 합쳐
//   타임라인에 조각조각이 아니라 한 줄(연속 띠)로 표시하기 위한 데이터.
export function buildTimelineRuns({
  segments,
  windowStartSec,
  windowEndSec,
  visibleWindowSeconds,
  gapTolSec = 12,
}) {
  if (!Array.isArray(segments) || segments.length === 0) return [];

  const sorted = [...segments].sort(
    (a, b) => getSegStartSec(a) - getSegStartSec(b)
  );

  const runs = [];
  let cur = {
    startSec: getSegStartSec(sorted[0]),
    endSec: getSegEndSec(sorted[0]),
  };

  for (let i = 1; i < sorted.length; i += 1) {
    const s = getSegStartSec(sorted[i]);
    const e = getSegEndSec(sorted[i]);

    if (s - cur.endSec <= gapTolSec) {
      // 연속 → 같은 띠로 확장
      if (e > cur.endSec) cur.endSec = e;
    } else {
      // 끊김 → 새 띠 시작
      runs.push(cur);
      cur = { startSec: s, endSec: e };
    }
  }
  runs.push(cur);

  return runs
    .map((run, idx) => {
      if (run.endSec < windowStartSec || run.startSec > windowEndSec) {
        return null;
      }

      const visibleStart = Math.max(run.startSec, windowStartSec);
      const visibleEnd = Math.min(run.endSec, windowEndSec);

      const leftPercent =
        ((visibleStart - windowStartSec) / visibleWindowSeconds) * 100;

      const widthPercent = Math.max(
        ((visibleEnd - visibleStart) / visibleWindowSeconds) * 100,
        0.4
      );

      return {
        id: `run-${idx}`,
        startSec: run.startSec,
        endSec: run.endSec,
        leftPercent,
        widthPercent,
      };
    })
    .filter(Boolean);
}

// 특정 시각(초)을 포함하는 녹화 조각을 찾음(정확히 그 시각에 찍힌 파일).
export function findSegmentContainingSec(segments, targetSec) {
  if (!Array.isArray(segments)) return null;

  for (const seg of segments) {
    if (targetSec >= getSegStartSec(seg) && targetSec <= getSegEndSec(seg)) {
      return seg;
    }
  }

  return null;
}

export function findClosestSegmentBySec(segments, targetSec) {
  if (!Array.isArray(segments) || segments.length === 0) return null;

  let best = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const seg of segments) {
    const startSec = getSegStartSec(seg);
    const endSec = getSegEndSec(seg);

    let distance = 0;

    if (targetSec < startSec) distance = startSec - targetSec;
    else if (targetSec > endSec) distance = targetSec - endSec;

    if (distance < bestDistance) {
      bestDistance = distance;
      best = seg;
    }
  }

  return best;
}

export function clampWindowStart(startSec, visibleWindowSeconds) {
  let next = startSec;

  if (next < 0) next = 0;

  if (next > 86400 - visibleWindowSeconds) {
    next = Math.max(0, 86400 - visibleWindowSeconds);
  }

  return Math.floor(next);
}